import React from 'react';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
    <div className="privacy-policy">
      <div className="privacy-container">
        <header className="privacy-header">
          <a href="/" className="back-link">← Back to Home</a>
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated: January 2024</p>
        </header>

        <div className="privacy-content">
          <section>
            <h2>1. Introduction</h2>
            <p>
              Welcome to Panchangam ("we," "our," or "us"). We are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and related services.
            </p>
            <p>
              By using Panchangam, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our application.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Personal Information</h3>
            <p>We may collect the following personal information:</p>
            <ul>
              <li><strong>Location Data:</strong> With your consent, we collect your location to provide accurate panchang calculations and location-based features.</li>
              <li><strong>Device Information:</strong> Device type, operating system, and app version for optimal functionality.</li>
              <li><strong>Usage Data:</strong> Information about how you use our app, including features accessed and interaction patterns.</li>
              <li><strong>Language Preferences:</strong> Your preferred language settings for displaying panchang information.</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <p>We automatically collect certain information when you use our app:</p>
            <ul>
              <li><strong>Device Information:</strong> Device type, operating system, and app version.</li>
              <li><strong>Log Data:</strong> Usage statistics, error reports, and performance data.</li>
              <li><strong>Analytics:</strong> Anonymous usage analytics to improve our services.</li>
              <li><strong>Timezone Information:</strong> Your device's timezone for accurate time calculations.</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li><strong>Provide Services:</strong> To deliver accurate panchang calculations, sun/moon timings, and festival information.</li>
              <li><strong>Location-Based Features:</strong> To provide location-specific panchang data and auspicious timings.</li>
              <li><strong>Personalization:</strong> To customize your experience and provide relevant features based on your location.</li>
              <li><strong>Improvement:</strong> To analyze usage patterns and improve our app functionality.</li>
              <li><strong>Communication:</strong> To send you important updates, notifications, and support messages.</li>
              <li><strong>Security:</strong> To protect against fraud, abuse, and security threats.</li>
            </ul>
          </section>

          <section>
            <h2>4. Information Sharing and Disclosure</h2>
            <p>We do not sell, trade, or otherwise transfer your personal information to third parties, except in the following circumstances:</p>
            <ul>
              <li><strong>Service Providers:</strong> We may share information with trusted third-party service providers who assist us in operating our app (e.g., location services, analytics).</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights and safety.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred.</li>
              <li><strong>With Your Consent:</strong> We may share information with your explicit consent.</li>
            </ul>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>We implement appropriate security measures to protect your personal information:</p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security assessments and updates</li>
              <li>Access controls and authentication measures</li>
              <li>Secure cloud storage and backup systems</li>
              <li>Local data caching for offline functionality</li>
            </ul>
            <p>However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.</p>
          </section>

          <section>
            <h2>6. Data Retention</h2>
            <p>We retain your personal information for as long as necessary to provide our services and fulfill the purposes outlined in this Privacy Policy. Location data is cached locally on your device and can be cleared through the app settings. You may request deletion of your account and associated data at any time.</p>
          </section>

          <section>
            <h2>7. Your Rights and Choices</h2>
            <p>You have the following rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> Request access to your personal information.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information.</li>
              <li><strong>Deletion:</strong> Request deletion of your account and data.</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format.</li>
              <li><strong>Opt-out:</strong> Opt out of certain data collection and communications.</li>
              <li><strong>Location Permissions:</strong> Control location access through your device settings.</li>
            </ul>
          </section>

          <section>
            <h2>8. Third-Party Services</h2>
            <p>Our app may integrate with third-party services:</p>
            <ul>
              <li><strong>Location Services:</strong> We use device location services for accurate panchang calculations.</li>
              <li><strong>Google Services:</strong> We may use Google services for location data and analytics.</li>
              <li><strong>Analytics:</strong> We use analytics services to understand app usage and improve our services.</li>
              <li><strong>Calendar Integration:</strong> Optional integration with your device's calendar for adding auspicious timings.</li>
            </ul>
            <p>These third-party services have their own privacy policies, and we encourage you to review them.</p>
          </section>

          <section>
            <h2>9. Children's Privacy</h2>
            <p>Our app is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you believe we have collected information from a child under 13, please contact us immediately.</p>
          </section>

          <section>
            <h2>10. International Data Transfers</h2>
            <p>Your information may be transferred to and processed in countries other than your own. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards.</p>
          </section>

          <section>
            <h2>11. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. We encourage you to review this Privacy Policy periodically.</p>
          </section>

          <section>
            <h2>12. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
            <ul>
              <li><strong>Email:</strong> feedback.innings@gmail.com</li>
              <li><strong>Developer:</strong> Swetha Reddy</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy; 